import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  initSessionEngagementTracking,
  trackCanvasUsedOnce,
} from "./analytics/engagement";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  usersIcon,
  share,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { DriveSceneReloadBanner } from "./components/DriveSceneReloadBanner";
import { DriveSyncButton } from "./components/DriveSyncButton";
import { SaveToBrowserOverwriteAction } from "./components/SaveToBrowserOverwriteAction";
import { SharedSceneBanner } from "./components/SharedSceneBanner";
import { TopErrorBoundary } from "./components/TopErrorBoundary";
import { useDriveAutoMerge } from "./components/useDriveAutoMerge";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import {
  GITHUB_REPO,
  isAIBackendEnabled,
  isCollabBackendEnabled,
  isContactFormEnabled,
  isDonateEnabled,
  isGoogleDriveShareEnabled,
  isOfficialShareBackendEnabled,
} from "./branding/constants";
import { ContactUsDialog } from "./contact/ContactUsDialog";
import { DonateModal } from "./donate/DonateModal";
import { DonateReminderModal } from "./donate/reminder/DonateReminderModal";
import {
  clearExpiredDonateThanksToast,
  DONATE_THANKS_TOAST_KEY,
  DONATE_THANKS_TOAST_TTL_MS,
} from "./donate/reminder/donateReminderService";
import { useDonateReminder } from "./donate/reminder/useDonateReminder";
import { CONTACT_US_OPEN_EVENT } from "./contact/openContactUs";
import {
  driveShareService,
  driveAutoMergeFailToastMessage,
  driveAutoSyncFailToastMessage,
  formatDriveMergeSuccessMessage,
  initDriveAuth,
  isGoogleDriveEnabled,
  parseShareFileIdFromLocation,
  registerDriveActiveSceneNeedsReloadNotifier,
  registerDriveAutoMergeFailedNotifier,
  registerDriveAutoMergeSuccessNotifier,
  registerDriveAutoSyncNotifier,
} from "./google-drive";
import {
  SceneVaultDialog,
  flushVaultSync,
  isSceneVaultEnabled,
  migrateLegacySceneAfterImagesLoaded,
  notifySceneVaultMigrationToast,
  SceneVaultClearCanvasDialog,
  sceneVaultQuotaExceededAtom,
  sceneVaultService,
  sceneVaultStore,
  scheduleVaultSync,
  setVaultOperationContext,
} from "./scene-vault";

import "./index.scss";

import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
  proceedOnSaveToBrowser: true,
} as const;

type InitializeSceneResult = { scene: ExcalidrawInitialDataState | null } & (
  | {
      isExternalScene: true;
      id: string;
      key: string;
      externalSource: "json" | "firebase";
      files?: BinaryFiles;
    }
  | {
      isExternalScene: true;
      driveFileId: string;
      externalSource: "drive-share";
      files?: BinaryFiles;
    }
  | { isExternalScene: false; id?: null; key?: null }
);

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<InitializeSceneResult> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const driveShareFileId = parseShareFileIdFromLocation(window.location.href);
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(
    id ||
    jsonBackendMatch ||
    roomLinkData ||
    driveShareFileId
  );
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (driveShareFileId) {
        try {
          const imported = await driveShareService.loadSharedScene(
            driveShareFileId,
          );
          scene = {
            elements: restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            appState: restoreAppState(imported.appState, localDataState?.appState),
          };
          scene.scrollToContent = true;
          window.history.replaceState({}, APP_NAME, window.location.pathname);
          return {
            scene,
            isExternalScene: true,
            driveFileId: driveShareFileId,
            externalSource: "drive-share",
            files: imported.files,
          };
        } catch (error: any) {
          return {
            scene: {
              appState: {
                errorMessage:
                  error?.message ||
                  "Could not open this shared drawing.",
              },
            },
            isExternalScene: true,
            driveFileId: driveShareFileId,
            externalSource: "drive-share",
          };
        }
      }
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
      externalSource: "firebase",
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
          externalSource: "json",
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled =
    isRunningInIframe() || !isCollabBackendEnabled();
  const isShareBackendEnabled = isOfficialShareBackendEnabled();
  const isDriveShareEnabled = isGoogleDriveShareEnabled();
  const showShareDialog = isDriveShareEnabled || isShareBackendEnabled;

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);
  const vaultMigrationRanRef = useRef(false);
  const liveElementCountRef = useRef(0);

  useEffect(() => {
    initSessionEngagementTracking();
    trackEvent("load", "frame", getFrame());
    if (isGoogleDriveEnabled()) {
      void initDriveAuth();
    }
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [sceneVaultDialogOpen, setSceneVaultDialogOpen] = useState(false);
  const [contactUsDialogOpen, setContactUsDialogOpen] = useState(false);
  const [donateModalOpen, setDonateModalOpen] = useState(false);

  const donateReminder = useDonateReminder({
    onOpenDonateModal: () => setDonateModalOpen(true),
  });

  useEffect(() => {
    if (!isContactFormEnabled()) {
      return;
    }
    const openContactUs = () => {
      excalidrawAPI?.updateScene({ appState: { openDialog: null } });
      setContactUsDialogOpen(true);
    };
    window.addEventListener(CONTACT_US_OPEN_EVENT, openContactUs);
    return () => {
      window.removeEventListener(CONTACT_US_OPEN_EVENT, openContactUs);
    };
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!isDonateEnabled()) {
      return;
    }
    const expireStaleToast = () => {
      clearExpiredDonateThanksToast();
    };
    expireStaleToast();
    const timer = window.setInterval(expireStaleToast, 60_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!excalidrawAPI || !isDonateEnabled()) {
      return;
    }
    try {
      const raw = sessionStorage.getItem(DONATE_THANKS_TOAST_KEY);
      if (!raw) {
        return;
      }
      const queuedAt = Number(raw);
      if (
        !Number.isFinite(queuedAt) ||
        Date.now() - queuedAt > DONATE_THANKS_TOAST_TTL_MS
      ) {
        sessionStorage.removeItem(DONATE_THANKS_TOAST_KEY);
        return;
      }
      sessionStorage.removeItem(DONATE_THANKS_TOAST_KEY);
    } catch {
      return;
    }
    excalidrawAPI.setToast({
      message: "Thank you for supporting diagrams.free!",
      duration: 5000,
    });
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || !isGoogleDriveEnabled()) {
      registerDriveAutoSyncNotifier(null);
      registerDriveAutoMergeSuccessNotifier(null);
      registerDriveAutoMergeFailedNotifier(null);
      registerDriveActiveSceneNeedsReloadNotifier(null);
      return;
    }
    registerDriveAutoSyncNotifier(() => {
      excalidrawAPI.setToast({
        message: driveAutoSyncFailToastMessage(),
        closable: true,
        duration: 6000,
      });
    });
    registerDriveAutoMergeFailedNotifier(() => {
      excalidrawAPI.setToast({
        message: driveAutoMergeFailToastMessage(),
        closable: true,
        duration: 6000,
      });
    });
    registerDriveAutoMergeSuccessNotifier((result) => {
      excalidrawAPI.setToast({
        message: formatDriveMergeSuccessMessage(result),
        closable: true,
        duration: 5000,
      });
    });
    registerDriveActiveSceneNeedsReloadNotifier((sceneId) => {
      setDriveSceneNeedsReload(sceneId);
    });
    return () => {
      registerDriveAutoSyncNotifier(null);
      registerDriveAutoMergeSuccessNotifier(null);
      registerDriveAutoMergeFailedNotifier(null);
      registerDriveActiveSceneNeedsReloadNotifier(null);
    };
  }, [excalidrawAPI]);

  const [activeVaultSceneId, setActiveVaultSceneId] = useState<string | null>(
    null,
  );
  const [driveSceneNeedsReload, setDriveSceneNeedsReload] = useState<
    string | null
  >(null);
  const [isExternalVaultScene, setIsExternalVaultScene] = useState(false);
  const vaultQuotaExceeded = useAtomValue(sceneVaultQuotaExceededAtom);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  const sceneVaultEnabled =
    isSceneVaultEnabled() &&
    !isRunningInIframe() &&
    !isCollaborating &&
    !isExternalVaultScene;

  const confirmActiveSceneReload = useCallback(async () => {
    return openConfirmModal({
      title: "Reload scene from Google Drive?",
      description:
        "Google Drive has a newer version of this scene. Reload it and replace what you see on the canvas?",
      actionLabel: "Reload from Drive",
      color: "warning",
    });
  }, []);

  useDriveAutoMerge({
    excalidrawAPI,
    enabled: sceneVaultEnabled && isGoogleDriveEnabled(),
  });

  useEffect(() => {
    setVaultOperationContext({
      isCollaborating: !!collabAPI?.isCollaborating(),
      isExternalScene: isExternalVaultScene,
    });
  }, [collabAPI, isCollaborating, isExternalVaultScene]);

  const refreshActiveVaultSceneId = useCallback(() => {
    if (!isSceneVaultEnabled()) {
      return;
    }
    void sceneVaultStore.getActiveSceneId().then(setActiveVaultSceneId);
  }, []);

  const runVaultMigrationOnce = useCallback(() => {
    if (!isSceneVaultEnabled() || vaultMigrationRanRef.current) {
      return;
    }
    vaultMigrationRanRef.current = true;
    void migrateLegacySceneAfterImagesLoaded().then((migrated) => {
      refreshActiveVaultSceneId();
      notifySceneVaultMigrationToast(excalidrawAPI, migrated);
    });
  }, [refreshActiveVaultSceneId, excalidrawAPI]);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (
          data.isExternalScene &&
          data.externalSource === "drive-share" &&
          data.files
        ) {
          const loadedFiles = Object.values(data.files);
          if (loadedFiles.length) {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles: new Map(),
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          }
        } else if (
          data.isExternalScene &&
          (data.externalSource === "json" || data.externalSource === "firebase")
        ) {
          if (fileIds.length) {
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            data.externalSource === "firebase"
              ? `files/rooms/${data.id}`
              : `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          const afterInitialImagesLoaded = () => {
            if (!data.isExternalScene) {
              runVaultMigrationOnce();
            }
          };

          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              })
              .finally(afterInitialImagesLoaded);
          } else {
            afterInitialImagesLoaded();
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI, runVaultMigrationOnce],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
      setIsExternalVaultScene(!!data.isExternalScene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          setIsExternalVaultScene(!!data.isExternalScene);
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
      if (sceneVaultEnabled && excalidrawAPI) {
        void flushVaultSync(excalidrawAPI);
      }
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
        if (sceneVaultEnabled && excalidrawAPI) {
          void flushVaultSync(excalidrawAPI);
        }
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, loadImages, sceneVaultEnabled]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();
      if (sceneVaultEnabled && excalidrawAPI) {
        void flushVaultSync(excalidrawAPI);
      }

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    const liveCount = elements.filter((el) => !el.isDeleted).length;
    if (liveCount > 0 && liveElementCountRef.current === 0) {
      trackCanvasUsedOnce("element_created");
    }
    liveElementCountRef.current = liveCount;

    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (
      sceneVaultEnabled &&
      excalidrawAPI &&
      !LocalData.isSavePaused()
    ) {
      scheduleVaultSync(excalidrawAPI);
    }

    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const onPointerUpdate = useCallback<
    NonNullable<ExcalidrawProps["onPointerUpdate"]>
  >(
    (payload) => {
      if (payload.button === "down") {
        trackCanvasUsedOnce("pointer");
      }
      collabAPI?.onPointerUpdate?.(payload);
    },
    [collabAPI],
  );

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={onPointerUpdate}
        UIOptions={{
          canvasActions: {
            clearCanvas: !sceneVaultEnabled,
            toggleTheme: true,
            export: {
              onExportToBackend:
                isShareBackendEnabled && !isDriveShareEnabled
                  ? onExportToBackend
                  : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          const driveSyncButton =
            isGoogleDriveEnabled() && excalidrawAPI && sceneVaultEnabled ? (
              <DriveSyncButton
                excalidrawAPI={excalidrawAPI}
                confirmActiveSceneReload={confirmActiveSceneReload}
                onError={(message) => setErrorMessage(message)}
                onSuccess={(message) => {
                  excalidrawAPI.setToast({
                    message,
                    duration: 5000,
                    closable: true,
                  });
                  refreshActiveVaultSceneId();
                }}
              />
            ) : null;

          if (isMobile) {
            return driveSyncButton ? (
              <div className="excalidraw-ui-top-right">{driveSyncButton}</div>
            ) : null;
          }

          if (isCollabDisabled && !isDriveShareEnabled && !driveSyncButton) {
            return null;
          }
          if (isCollabDisabled && isDriveShareEnabled) {
            return (
              <div className="excalidraw-ui-top-right">
                {driveSyncButton}
                <LiveCollaborationTrigger
                  isCollaborating={false}
                  onSelect={() =>
                    setShareDialogState({ isOpen: true, type: "share" })
                  }
                  editorInterface={editorInterface}
                />
              </div>
            );
          }
          if (!collabAPI) {
            return driveSyncButton ? (
              <div className="excalidraw-ui-top-right">{driveSyncButton}</div>
            ) : null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {driveSyncButton}
              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          sceneVaultEnabled={sceneVaultEnabled}
          onOpenSceneVault={() => setSceneVaultDialogOpen(true)}
          onSaveToBrowser={() => {
            if (excalidrawAPI) {
              void sceneVaultService
                .archiveCurrentScene(excalidrawAPI)
                .then(() => {
                  refreshActiveVaultSceneId();
                });
            }
          }}
          onNewCanvas={() => {
            if (excalidrawAPI) {
              void sceneVaultService
                .newCanvas(excalidrawAPI, "menu")
                .then(() => {
                  refreshActiveVaultSceneId();
                });
            }
          }}
          onResetCanvas={() => {
            if (!excalidrawAPI) {
              return;
            }
            void openConfirmModal({
              title: "Delete current scene?",
              description:
                "This will permanently delete the current scene and clear the canvas. This cannot be undone.",
              actionLabel: "Delete",
              color: "danger",
              resetAfterSaveToBrowser: true,
            }).then((confirmed) => {
              if (confirmed) {
                void sceneVaultService
                  .resetCanvas(excalidrawAPI)
                  .then(() => {
                    refreshActiveVaultSceneId();
                  });
              }
            });
          }}
          onOpenDonate={() => setDonateModalOpen(true)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
          sceneVaultEnabled={sceneVaultEnabled}
          onOpenSceneVault={() => setSceneVaultDialogOpen(true)}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <SaveToBrowserOverwriteAction
            onSaved={refreshActiveVaultSceneId}
          />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && isAIBackendEnabled() && (
          <AIComponents excalidrawAPI={excalidrawAPI} />
        )}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {vaultQuotaExceeded && isSceneVaultEnabled() && (
          <div className="alert alert--danger">
            Scene vault storage is full. Open My scenes to delete or download
            saved drawings and free space.
          </div>
        )}
        {excalidrawAPI &&
        driveSceneNeedsReload &&
        activeVaultSceneId === driveSceneNeedsReload &&
        !isExternalVaultScene ? (
          <DriveSceneReloadBanner
            excalidrawAPI={excalidrawAPI}
            sceneId={driveSceneNeedsReload}
            onReloaded={() => {
              setDriveSceneNeedsReload(null);
              refreshActiveVaultSceneId();
              excalidrawAPI.setToast({
                message: "Scene reloaded from Google Drive.",
                duration: 4000,
              });
            }}
            onDismiss={() => setDriveSceneNeedsReload(null)}
          />
        ) : null}
        {excalidrawAPI && isExternalVaultScene && (
          <SharedSceneBanner
            excalidrawAPI={excalidrawAPI}
            onSavedToVault={() => {
              setIsExternalVaultScene(false);
              refreshActiveVaultSceneId();
              excalidrawAPI.setToast({
                message: "Saved to My scenes.",
                duration: 4000,
              });
            }}
          />
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {isDonateEnabled() && (
          <>
            <DonateReminderModal
              isOpen={donateReminder.isOpen}
              onSupport={donateReminder.handleSupport}
              onSnoozeMonth={donateReminder.handleSnoozeMonth}
              onClose={donateReminder.handleClose}
            />
            <DonateModal
              isOpen={donateModalOpen}
              onClose={() => setDonateModalOpen(false)}
            />
          </>
        )}
        {excalidrawAPI && isContactFormEnabled() && (
          <ContactUsDialog
            isOpen={contactUsDialogOpen}
            onClose={() => setContactUsDialogOpen(false)}
            excalidrawAPI={excalidrawAPI}
          />
        )}
        {excalidrawAPI && sceneVaultEnabled && (
          <>
            <SceneVaultDialog
              isOpen={sceneVaultDialogOpen}
              onClose={() => setSceneVaultDialogOpen(false)}
              excalidrawAPI={excalidrawAPI}
              activeSceneId={activeVaultSceneId}
              onScenesChange={refreshActiveVaultSceneId}
              confirmActiveSceneReload={confirmActiveSceneReload}
              onDriveMergeSuccess={(message) => {
                excalidrawAPI.setToast({
                  message,
                  duration: 5000,
                  closable: true,
                });
              }}
            />
            <SceneVaultClearCanvasDialog
              enabled={sceneVaultEnabled}
              excalidrawAPI={excalidrawAPI}
              onAfterClear={refreshActiveVaultSceneId}
            />
          </>
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        {showShareDialog && (
          <ShareDialog
            collabAPI={isCollabDisabled ? null : collabAPI}
            isDriveShareEnabled={isDriveShareEnabled}
            onExportToBackend={
              isShareBackendEnabled
                ? async () => {
                    if (excalidrawAPI) {
                      try {
                        await onExportToBackend(
                          excalidrawAPI.getSceneElements(),
                          excalidrawAPI.getAppState(),
                          excalidrawAPI.getFiles(),
                        );
                      } catch (error: any) {
                        setErrorMessage(error.message);
                      }
                    }
                  }
                : undefined
            }
            onCreateDriveShareLink={
              isDriveShareEnabled && excalidrawAPI
                ? async () => {
                    const result =
                      await driveShareService.createShareLink(excalidrawAPI);
                    setLatestShareableLink(result.url);
                  }
                : undefined
            }
          />
        )}

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            ...(isCollabDisabled
              ? []
              : [
                  {
                    label: t("labels.liveCollaboration"),
                    category: DEFAULT_CATEGORIES.app,
                    keywords: [
                      "team",
                      "multiplayer",
                      "share",
                      "public",
                      "session",
                      "invite",
                    ],
                    icon: usersIcon,
                    perform: () => {
                      setShareDialogState({
                        isOpen: true,
                        type: "collaborationOnly",
                      });
                    },
                  },
                  {
                    label: t("roomDialog.button_stopSession"),
                    category: DEFAULT_CATEGORIES.app,
                    predicate: () => !!collabAPI?.isCollaborating(),
                    keywords: [
                      "stop",
                      "session",
                      "end",
                      "leave",
                      "close",
                      "exit",
                      "collaboration",
                    ],
                    perform: () => {
                      if (collabAPI) {
                        collabAPI.stopCollaboration();
                        if (!collabAPI.isCollaborating()) {
                          setShareDialogState({ isOpen: false });
                        }
                      }
                    },
                  },
                ]),
            ...(showShareDialog
              ? [
                  {
                    label: t("labels.share"),
                    category: DEFAULT_CATEGORIES.app,
                    predicate: true,
                    icon: share,
                    keywords: [
                      "link",
                      "shareable",
                      "readonly",
                      "export",
                      "publish",
                      "snapshot",
                      "url",
                      "collaborate",
                      "invite",
                    ],
                    perform: async () => {
                      setShareDialogState({ isOpen: true, type: "share" });
                    },
                  },
                ]
              : []),
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "source",
                "repository",
              ],
              perform: () => {
                window.open(GITHUB_REPO, "_blank", "noopener noreferrer");
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
